import { CustomDrawer } from '@/components/common/CustomDrawer';
import { ReusableDataGrid } from '@/components/common/ReusableDataGrid';
import { USER_ROLES } from '@/constants/app';
import {
  Booking,
  BookingFormData,
  Facility,
  Fraternity,
} from '@/constants/types';
import { useAuth } from '@/hooks';
import { bookingService, facilityService, fraternityService } from '@/services';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import {
  CalendarMonth as CalendarMonthIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  // Lock as LockIcon,
  Search as SearchIcon,
  TableChart as TableChartIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useFormik } from 'formik';
import type { MRT_ColumnDef } from 'material-react-table';
import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import * as Yup from 'yup';

const validationSchema = Yup.object({
  fraternityId: Yup.string().required('Fraternity is required'),
  date: Yup.string().required('Date is required'),
  // facilities: Yup.array()
  //   .of(
  //     Yup.object().shape({
  //       facilityId: Yup.string().required('Facility is required'),
  //       time: Yup.string().required('Time is required'),
  //     })
  //   )
  //   .min(1, 'At least one facility is required')
  //   .max(2, 'Maximum 2 facilities allowed')
  //   .required('Facilities are required'),
});

type ViewMode = 'table' | 'calendar';

export const BookingsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();

  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [fraternityFilter, setFraternityFilter] = useState('');
  const [facilityFilter, setFacilityFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);

  // Dropdown data
  const [fraternities, setFraternities] = useState<Fraternity[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loadingFraternities, setLoadingFraternities] = useState(false);
  const [loadingFacilities, setLoadingFacilities] = useState(false);

  // Date and time state (common date for all facilities)
  const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
  const [availableTimeSlots1, setAvailableTimeSlots1] = useState<string[]>([]);
  const [availableTimeSlots2, setAvailableTimeSlots2] = useState<string[]>([]);

  // Formik setup
  const formik = useFormik<BookingFormData>({
    initialValues: {
      fraternityId: '',
      date: '',
      facilities: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (isEditMode && selectedBooking) {
          await bookingService.update(selectedBooking._id, values);
          toast.success('Booking updated successfully');
        } else {
          await bookingService.add(values);
          toast.success('Booking added successfully');
        }
        handleCloseDrawer();
        fetchBookings();
      } catch (error: any) {
        toast.error(error.message || 'Failed to save booking');
      }
    },
  });

  // Fetch bookings
  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      // Prepare date range if dateFilter is set
      let startDate = '';
      let endDate = '';
      if (dateFilter) {
        const selectedMoment = moment(dateFilter);
        startDate = selectedMoment.startOf('day').toISOString();
        endDate = selectedMoment.endOf('day').toISOString();
      }

      const response = await bookingService.list({
        sortDir: 'desc',
        limit: 1000,
        offset: 0,
        search_string: searchTerm,
        sortBy: 'createdAt',
        facilityId: facilityFilter || undefined,
        fraternityId: fraternityFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });

      if (response.detail && response.detail[0]) {
        setBookings(response.detail[0].paginatedResults);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, facilityFilter, fraternityFilter, dateFilter]);

  // Fetch fraternities
  const fetchFraternities = useCallback(async () => {
    setLoadingFraternities(true);
    try {
      const response = await fraternityService.list({
        sortDir: 'asc',
        limit: 1000,
        offset: 0,
        search_string: '',
        sortBy: 'name',
      });

      if (response.detail && response.detail[0]) {
        setFraternities(response.detail[0].paginatedResults);
      }
    } catch (error) {
      console.error('Error fetching fraternities:', error);
      toast.error('Failed to fetch fraternities');
    } finally {
      setLoadingFraternities(false);
    }
  }, []);

  // Fetch facilities
  const fetchFacilities = useCallback(async () => {
    setLoadingFacilities(true);
    try {
      const response = await facilityService.list({
        sortDir: 'asc',
        limit: 1000,
        offset: 0,
        search_string: '',
        sortBy: 'name',
      });

      if (response.detail && response.detail[0]) {
        const activeFacilities = response.detail[0].paginatedResults.filter(
          (facility) => facility.isActive
        );
        setFacilities(activeFacilities);
      }
    } catch (error) {
      console.error('Error fetching facilities:', error);
      toast.error('Failed to fetch facilities');
    } finally {
      setLoadingFacilities(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    fetchFraternities();
    fetchFacilities();
  }, [fetchFraternities, fetchFacilities]);

  // Use bookings directly since filtering is now done on server-side
  const filteredBookings = useMemo(() => {
    return bookings;
  }, [bookings]);

  // Get selected facility details for each facility
  const selectedFacility1 = useMemo(() => {
    if (formik.values.facilities[0]?.facilityId) {
      return facilities.find((f) => f._id === formik.values.facilities[0].facilityId);
    }
    return null;
  }, [formik.values.facilities, facilities]);

  const selectedFacility2 = useMemo(() => {
    if (formik.values.facilities[1]?.facilityId) {
      return facilities.find((f) => f._id === formik.values.facilities[1].facilityId);
    }
    return null;
  }, [formik.values.facilities, facilities]);

  // Filter facilities by type
  const templeFacilities = useMemo(() => {
    return facilities.filter((f) => f.type === 'temple');
  }, [facilities]);

  const hallFacilities = useMemo(() => {
    return facilities.filter((f) => f.type === 'hall');
  }, [facilities]);

  // Update available time slots for facility 1
  useEffect(() => {
    if (selectedFacility1 && selectedDate) {
      const dateStr = selectedDate.format('YYYY-MM-DD');
      const nonAvailableDates = selectedFacility1.nonAvailableDates || [];
      const availableSlots = selectedFacility1.availableSlots || [];

      if (!nonAvailableDates.includes(dateStr)) {
        setAvailableTimeSlots1(availableSlots);
      } else {
        setAvailableTimeSlots1([]);
        toast.error('Selected date is not available for this facility');
      }

      if (formik.values.facilities[0]?.time && !availableSlots.includes(formik.values.facilities[0].time)) {
        const updatedFacilities = [...formik.values.facilities];
        updatedFacilities[0] = { ...updatedFacilities[0], time: '' };
        formik.setFieldValue('facilities', updatedFacilities);
      }
    } else if (selectedFacility1 && !selectedDate) {
      setAvailableTimeSlots1(selectedFacility1.availableSlots || []);
    } else {
      setAvailableTimeSlots1([]);
    }
  }, [selectedFacility1, selectedDate]);

  // Update available time slots for facility 2
  useEffect(() => {
    if (selectedFacility2 && selectedDate) {
      const dateStr = selectedDate.format('YYYY-MM-DD');
      const nonAvailableDates = selectedFacility2.nonAvailableDates || [];
      const availableSlots = selectedFacility2.availableSlots || [];

      if (!nonAvailableDates.includes(dateStr)) {
        setAvailableTimeSlots2(availableSlots);
      } else {
        setAvailableTimeSlots2([]);
        toast.error('Selected date is not available for this facility');
      }

      if (formik.values.facilities[1]?.time && !availableSlots.includes(formik.values.facilities[1].time)) {
        const updatedFacilities = [...formik.values.facilities];
        updatedFacilities[1] = { ...updatedFacilities[1], time: '' };
        formik.setFieldValue('facilities', updatedFacilities);
      }
    } else if (selectedFacility2 && !selectedDate) {
      setAvailableTimeSlots2(selectedFacility2.availableSlots || []);
    } else {
      setAvailableTimeSlots2([]);
    }
  }, [selectedFacility2, selectedDate]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setFraternityFilter('');
    setFacilityFilter('');
    setDateFilter('');
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleOpenDrawer = (booking?: Booking) => {
    if (booking) {
      setIsEditMode(true);
      setSelectedBooking(booking);

      // Use the facilities data from booking
      const facilitiesData = booking.facilities?.map((facility) => ({
        facilityId: facility?.facilityId || '',
        time: facility?.time || '',
      })) || [];

      formik.setValues({
        fraternityId: booking.fraternityId || '',
        date: booking.date || '',
        facilities: facilitiesData,
      });

      if (booking.date) {
        setSelectedDate(moment(booking.date));
      }
    } else {
      setIsEditMode(false);
      setSelectedBooking(null);
      formik.resetForm();
      setSelectedDate(null);
    }
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedBooking(null);
    setIsEditMode(false);
    setIsViewMode(false);
    formik.resetForm();
    setSelectedDate(null);
    setAvailableTimeSlots1([]);
    setAvailableTimeSlots2([]);
  };

  const handleEditFromView = () => {
    if (selectedBooking) {
      setIsViewMode(false);
      setIsEditMode(true);

      // Use the facilities data from booking
      const facilitiesData = selectedBooking.facilities?.map((facility) => ({
        facilityId: facility?.facilityId || '',
        time: facility?.time || '',
      })) || [];

      formik.setValues({
        fraternityId: selectedBooking.fraternityId || '',
        date: selectedBooking.date || '',
        facilities: facilitiesData,
      });

      if (selectedBooking.date) {
        setSelectedDate(moment(selectedBooking.date));
      }
    }
  };

  const handleDateChange = (date: Moment | null) => {
    setSelectedDate(date);
    if (date) {
      formik.setFieldValue('date', date.toISOString());
      // Reset time slots when date changes
      const updatedFacilities = formik.values.facilities.map((facility) => ({
        ...facility,
        time: '',
      }));
      formik.setFieldValue('facilities', updatedFacilities);
    } else {
      formik.setFieldValue('date', '');
    }
  };

  const handleOpenDeleteDialog = (booking: Booking) => {
    setSelectedBooking(booking);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedBooking(null);
  };

  const handleDelete = async () => {
    if (!selectedBooking) return;

    try {
      await bookingService.delete(selectedBooking._id);
      toast.success('Booking deleted successfully');
      handleCloseDeleteDialog();
      fetchBookings();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete booking');
    }
  };
 // Assign colors to overlapping events
  const getEventColor = (booking: Booking): string => {
    const colors = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c62828', '#0097a7'];

    // Group bookings by date and facility time slots
    const timeSlots = filteredBookings.reduce((acc, b) => {
      if (b.date) {
        b.facilities?.forEach((facility) => {
          if (facility?.time) {
            const key = `${b.date}-${facility.time}`;
            if (!acc[key]) {
              acc[key] = [];
            }
            acc[key].push(b._id);
          }
        });
      }
      return acc;
    }, {} as Record<string, string[]>);

    // Check if any facility in this booking has overlapping times
    if (booking.date && booking.facilities) {
      for (const facility of booking.facilities) {
        if (facility?.time) {
          const key = `${booking.date}-${facility.time}`;
          const bookingsAtSameTime = timeSlots[key] || [];
          if (bookingsAtSameTime.length > 1) {
            const index = bookingsAtSameTime.indexOf(booking._id);
            return colors[index % colors.length];
          }
        }
      }
    }

    return '#1976d2'; // Default blue
  };
  // Convert bookings to calendar events - one event per booking
  const calendarEvents = useMemo(() => {
    const events: any[] = [];

    filteredBookings.forEach(booking => {
      const fraternityName = booking.fraternityDetails?.[0]?.name || 'Unknown';

      // Skip if booking has no date
      if (!booking.date) {
        console.warn('Booking missing date:', booking.bookingId);
        return;
      }

      // Get all facility names for the title
      const facilityNames = booking.facilities
        ?.map(f => f?.facilityDetails?.name || 'TBB')
        .filter(Boolean)
        .join(', ') || 'TBB';

      // Use the first facility's time for event timing, or create all-day event
      const firstFacility = booking.facilities?.[0];
      let startDateTime, endDateTime;

      if (firstFacility?.time) {
        // Try multiple time format patterns
        const timePatterns = [
          /(\d{1,2}:\d{2}\s*(?:AM|PM))\s*-\s*(\d{1,2}:\d{2}\s*(?:AM|PM))/i,  // "9:00 AM - 10:00 AM"
          /(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/,  // "09:00 - 10:00" (24hr)
        ];

        let timeMatch = null;
        for (const pattern of timePatterns) {
          timeMatch = firstFacility.time.match(pattern);
          if (timeMatch) break;
        }

        if (timeMatch) {
          const [_, startTime, endTime] = timeMatch;

          // Try parsing with AM/PM first, then 24-hour format
          const formats = ['YYYY-MM-DD hh:mm A', 'YYYY-MM-DD HH:mm'];
          const dateStr = moment(booking.date).format('YYYY-MM-DD');

          startDateTime = moment(`${dateStr} ${startTime}`, formats, true);
          endDateTime = moment(`${dateStr} ${endTime}`, formats, true);

          if (!startDateTime.isValid() || !endDateTime.isValid()) {
            // Fallback to all-day event
            startDateTime = moment(booking.date).startOf('day');
            endDateTime = moment(booking.date).endOf('day');
          }
        } else {
          // Fallback to all-day event
          startDateTime = moment(booking.date).startOf('day');
          endDateTime = moment(booking.date).endOf('day');
        }
      } else {
        // No time specified, create all-day event
        startDateTime = moment(booking.date).startOf('day');
        endDateTime = moment(booking.date).endOf('day');
      }

      events.push({
        id: booking._id,
        title: `${fraternityName} - ${facilityNames}`,
        start: startDateTime.toISOString(),
        end: endDateTime.toISOString(),
        backgroundColor: getEventColor(booking),
        borderColor: getEventColor(booking),
        extendedProps: {
          bookingId: booking.bookingId,
          actualBookingId: booking._id,
          facilities: facilityNames,
        },
      });
    });

    console.log('Calendar events generated:', events.length);
    return events;
  }, [filteredBookings]);

  const handleEventClick = (eventId: string) => {
    const booking = filteredBookings.find(b => b._id === eventId);
    if (booking) {
      setSelectedBooking(booking);
      setIsViewMode(true);
      setIsEditMode(false);
      setDrawerOpen(true);
    }
  };

  // Check if current user can edit/delete a booking
  // const canModifyBooking = (booking: Booking): boolean => {
  //   if (!user) return false;
  //   return booking.createdBy === user._id;
  // };

  // Check if user has admin privileges
  const isAdmin = user?.role === USER_ROLES.Booking_SuperAdmin || user?.role === USER_ROLES.Booking_Admin;

  const columns: MRT_ColumnDef<Booking>[] = [
    {
      accessorKey: 'bookingId',
      header: 'Booking ID',
      size: 150,
    },
    {
      accessorKey: 'fraternityDetails',
      header: 'Fraternity',
      size: 200,
      Cell: ({ row }) => {
        const fraternity = row.original.fraternityDetails?.[0];
        return fraternity ? (
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {fraternity.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              #{fraternity.number}
            </Typography>
          </Box>
        ) : (
          '-'
        );
      },
    },
    {
      accessorKey: 'facilities',
      header: 'Facilities & Schedule',
      size: 300,
      Cell: ({ row }) => {
        const booking = row.original;
        const facilities = booking.facilities || [];
        return (
          <Stack spacing={1}>
            <Typography variant="caption" display="block" color="text.secondary" fontWeight={600}>
              {booking.date ? moment(booking.date).format('MMM DD, YYYY') : 'N/A'}
            </Typography>
            {facilities.map((facility, index) => (
              <Box key={index}>
                <Chip
                  label={facility?.facilityDetails?.name || 'TBB'}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ mb: 0.5 }}
                />
                <Typography variant="caption" display="block" color="text.secondary">
                  {facility?.time || 'N/A'}
                </Typography>
              </Box>
            ))}
          </Stack>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      Cell: ({ row }: { row: any }) => {
        const handleViewBooking = () => {
          setSelectedBooking(row.original);
          setIsViewMode(true);
          setIsEditMode(false);
          setDrawerOpen(true);
        };

        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isAdmin ? (
              <>
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenDrawer(row.original)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleOpenDeleteDialog(row.original)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Tooltip title="View Details">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleViewBooking}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    } as MRT_ColumnDef<Booking>,
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box>
        {/* Header */}
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" component="h5">
              Bookings Management
            </Typography>

          {isAdmin &&  <Box display="flex" gap={2} alignItems="center">
         

              <Button
                variant="contained"
                onClick={() => handleOpenDrawer()}
              >
                Add Booking
              </Button>
            </Box>}
          </Box>
          <Divider />
        </Box>
   <Box display="flex" gap={2} alignItems="center" justifyContent={"flex-end"}>
              {/* View Toggle */}
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
                aria-label="view mode"
              >
                <ToggleButton value="table" aria-label="table view">
                  <TableChartIcon sx={{ mr: isMobile ? 0 : 1 }} />
                  {!isMobile && 'Table'}
                </ToggleButton>
                <ToggleButton value="calendar" aria-label="calendar view">
                  <CalendarMonthIcon sx={{ mr: isMobile ? 0 : 1 }} />
                  {!isMobile && 'Calendar'}
                </ToggleButton>
              </ToggleButtonGroup>

             
            </Box>
        {/* Filters */}
        <Grid container spacing={2} alignItems="center" mb={3}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Search Bookings"
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPaginationModel({ ...paginationModel, page: 0 });
              }}
              placeholder="Search by Booking ID"
              slotProps={{
                input: {
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              select
              label="Fraternity"
              variant="outlined"
              size="small"
              fullWidth
              value={fraternityFilter}
              onChange={(e) => {
                setFraternityFilter(e.target.value);
                setPaginationModel({ ...paginationModel, page: 0 });
              }}
            >
              <MenuItem value="">All Fraternities</MenuItem>
              {fraternities.map((frat) => (
                <MenuItem key={frat._id} value={frat._id}>
                  {frat.name} - #{frat.number}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              select
              label="Facility"
              variant="outlined"
              size="small"
              fullWidth
              value={facilityFilter}
              onChange={(e) => {
                setFacilityFilter(e.target.value);
                setPaginationModel({ ...paginationModel, page: 0 });
              }}
            >
              <MenuItem value="">All Facilities</MenuItem>
              {facilities.map((facility) => (
                <MenuItem key={facility._id} value={facility._id}>
                  {facility.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              label="Date"
              type="date"
              variant="outlined"
              size="small"
              fullWidth
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setPaginationModel({ ...paginationModel, page: 0 });
              }}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 1 }}>
            <Button
              variant="text"
              onClick={handleClearFilters}
              startIcon={<ClearIcon />}
              title="Clear All Filters"
              sx={{
                '& .MuiButton-startIcon': {
                  mr: 0,
                  ml: 0,
                  display: 'flex',
                  justifyContent: 'end',
                },
              }}
              disabled={
                searchTerm === '' &&
                fraternityFilter === '' &&
                facilityFilter === '' &&
                dateFilter === ''
              }
            />
          </Grid>
        </Grid>

        {/* Content - Table or Calendar View */}
        {viewMode === 'table' ? (
          <ReusableDataGrid<Booking>
            columns={columns}
            rows={filteredBookings}
            loading={false}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            paginationData={{
              totalRecords: filteredBookings.length,
            }}
            enableServerSidePagination={false}
            enableColumnFilters={false}
            enableGlobalFilter={false}
            enableSorting={true}
            getRowId={(row) => row._id}
            showSerialNumber={true}
          />
        ) : (
          <Paper sx={{ p: 2, position: 'relative', minHeight: 400 }}>
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView={isMobile ? 'dayGridMonth' : 'dayGridMonth'}
              headerToolbar={{
                left: isMobile ? 'title' : 'today',
                center: isMobile ? '' : 'title',
                right: isMobile ? 'prev,next' : 'dayGridMonth,timeGridWeek,timeGridDay prev,next',
              }}
              events={calendarEvents}
              height="auto"
              eventDisplay="block"
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              }}
              eventClick={(info) => {
                // if(isAdmin){
                handleEventClick(info.event.id);
                // }
              }}
              buttonText={{
                today: 'Today',
                month: 'Month',
                week: 'Week',
                day: 'Day',
              }}
              slotMinTime="06:00:00"
              slotMaxTime="23:00:00"
              fixedWeekCount={false}
              showNonCurrentDates={false}
            />
          </Paper>
        )}

        {/* Add/Edit/View Drawer */}
        <CustomDrawer
          open={drawerOpen}
          onClose={handleCloseDrawer}
          title={isViewMode ? 'Booking Details' : isEditMode ? 'Edit Booking' : 'Add Booking'}
          width={isMobile?'100%':500}
        >
          {isViewMode && selectedBooking ? (
            /* View Mode */
            <Stack spacing={3}>
              {/* Booking ID */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Booking ID
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedBooking.bookingId}
                </Typography>
              </Box>

              <Divider />

              {/* Fraternity */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Fraternity
                </Typography>
                {selectedBooking.fraternityDetails?.[0] ? (
                  <>
                    <Typography variant="body1" fontWeight={600}>
                      {selectedBooking.fraternityDetails[0].name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      #{selectedBooking.fraternityDetails[0].number}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    N/A
                  </Typography>
                )}
              </Box>

              {/* Date */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Date
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedBooking.date ? moment(selectedBooking.date).format('dddd, MMMM DD, YYYY') : 'N/A'}
                </Typography>
              </Box>

              {/* Facilities */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Facilities & Schedule
                </Typography>
                {selectedBooking.facilities && selectedBooking.facilities.length > 0 ? (
                  <Stack spacing={2}>
                    {selectedBooking.facilities.map((facility, index) => (
                      <Box key={index} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Chip
                          label={facility?.facilityDetails?.name || 'TBB'}
                          size="small"
                          variant="outlined"
                          color={facility?.facilityDetails?.type === 'temple' ? 'primary' : 'secondary'}
                        />
                        <Chip
                          label={facility?.time || 'N/A'}
                          size="small"
                          variant="filled"
                          color="info"
                        />
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    N/A
                  </Typography>
                )}
              </Box>

              {/* Created Info */}
              <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Created: {moment(selectedBooking.createdAt).format('MMM DD, YYYY hh:mm A')}
                </Typography>
                {selectedBooking.updatedAt !== selectedBooking.createdAt && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Updated: {moment(selectedBooking.updatedAt).format('MMM DD, YYYY hh:mm A')}
                  </Typography>
                )}
              </Box>

              <Divider />

              {/* Action Buttons - Only show for admin users */}
              {isAdmin && selectedBooking && (
                <Box display="flex" gap={2}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleEditFromView}
                    startIcon={<EditIcon />}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    onClick={() => {
                      handleCloseDrawer();
                      handleOpenDeleteDialog(selectedBooking);
                    }}
                    startIcon={<DeleteIcon />}
                  >
                    Delete
                  </Button>
                </Box>
              )}
            </Stack>
          ) : (
            /* Add/Edit Form */
            <>
            <Stack spacing={3}>
              {/* Fraternity Selection */}
              <Autocomplete
                options={fraternities}
                getOptionLabel={(option) => `${option.name} - #${option.number} -- ${option.orderDetails?.[0]?.name || ''}`}
                value={fraternities.find((f) => f._id === formik.values.fraternityId) || null}
                onChange={(_, newValue) => {
                  formik.setFieldValue('fraternityId', newValue?._id || '');
                }}
                loading={loadingFraternities}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Fraternity"
                    required
                    error={formik.touched.fraternityId && Boolean(formik.errors.fraternityId)}
                    helperText={formik.touched.fraternityId && formik.errors.fraternityId}
                  />
                )}
              />

              {/* Common Date Picker */}
              <DatePicker
                label="Booking Date"
                value={selectedDate}
                onChange={(date) => handleDateChange(date)}
                format='DD/MM/YYYY'
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    size:"small",
                    error: formik.touched.date && Boolean(formik.errors.date),
                    helperText: formik.touched.date && formik.errors.date,
                  },
                }}
                // minDate={moment()}
              />

              <Divider>
                <Typography variant="caption" color="text.secondary">Facility Bookings</Typography>
              </Divider>

              {/* Facility 1 (Temple) */}
              <Box>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  Facility 1 - Temple
                </Typography>
                <Stack spacing={2}>
                  <Autocomplete
                    options={templeFacilities}
                    getOptionLabel={(option) => option.name}
                    value={templeFacilities.find((f) => f._id === formik.values.facilities[0]?.facilityId) || null}
                    onChange={(_, newValue) => {
                      const updatedFacilities = [...formik.values.facilities];
                      if (newValue) {
                        updatedFacilities[0] = {
                          facilityId: newValue._id,
                          time: updatedFacilities[0]?.time || '',
                        };
                      } else {
                        updatedFacilities.splice(0, 1);
                      }
                      formik.setFieldValue('facilities', updatedFacilities);
                      if (updatedFacilities[0]) {
                        updatedFacilities[0].time = '';
                      }
                    }}
                    loading={loadingFacilities}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Temple Facility"
                        error={
                          formik.touched.facilities?.[0]?.facilityId &&
                          Boolean((formik.errors.facilities as any)?.[0]?.facilityId)
                        }
                        helperText={
                          formik.touched.facilities?.[0]?.facilityId &&
                          (formik.errors.facilities as any)?.[0]?.facilityId
                        }
                      />
                    )}
                  />

                  {selectedFacility1 && (
                    <>
                      {/* Show facility info */}
                      <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Available Time Slots:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                          {(selectedFacility1.availableSlots || []).map((slot: string, index: number) => (
                            <Chip
                              key={index}
                              label={slot}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          ))}
                        </Box>
                      </Box>

                      {/* Time Selection */}
                      <TextField
                        select
                        label="Time Slot"
                        fullWidth
                        size="small"
                        value={formik.values.facilities[0]?.time || ''}
                        onChange={(e) => {
                          const updatedFacilities = [...formik.values.facilities];
                          updatedFacilities[0] = {
                            ...updatedFacilities[0],
                            time: e.target.value,
                          };
                          formik.setFieldValue('facilities', updatedFacilities);
                        }}
                        error={
                          formik.touched.facilities?.[0]?.time &&
                          Boolean((formik.errors.facilities as any)?.[0]?.time)
                        }
                        helperText={
                          formik.touched.facilities?.[0]?.time &&
                          (formik.errors.facilities as any)?.[0]?.time
                            ? (formik.errors.facilities as any)?.[0]?.time
                            : !selectedDate
                            ? 'Please select a date first'
                            : availableTimeSlots1.length === 0
                            ? 'No available time slots for selected date'
                            : `${availableTimeSlots1.length} time slot(s) available`
                        }
                        disabled={!selectedDate || availableTimeSlots1.length === 0}
                      >
                        {availableTimeSlots1.map((slot: string) => (
                          <MenuItem key={slot} value={slot}>
                            {slot}
                          </MenuItem>
                        ))}
                      </TextField>
                    </>
                  )}
                </Stack>
              </Box>

              <Divider />

              {/* Facility 2 (Hall) */}
              <Box>
                <Typography variant="subtitle2"  gutterBottom color="primary">
                  Facility 2 - Hall 
                </Typography>
                <Stack spacing={2}>
                  <Autocomplete
                    options={hallFacilities}
                    getOptionLabel={(option) => option.name}
                    value={hallFacilities.find((f) => f._id === formik.values.facilities[1]?.facilityId) || null}
                    onChange={(_, newValue) => {
                      const updatedFacilities = [...formik.values.facilities];
                      if (newValue) {
                        updatedFacilities[1] = {
                          facilityId: newValue._id,
                          time: updatedFacilities[1]?.time || '',
                        };
                      } else {
                        updatedFacilities.splice(1, 1);
                      }
                      formik.setFieldValue('facilities', updatedFacilities);
                      if (updatedFacilities[1]) {
                        updatedFacilities[1].time = '';
                      }
                    }}
                    loading={loadingFacilities}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Hall Facility"
                        error={
                          formik.touched.facilities?.[1]?.facilityId &&
                          Boolean((formik.errors.facilities as any)?.[1]?.facilityId)
                        }
                        helperText={
                          formik.touched.facilities?.[1]?.facilityId &&
                          (formik.errors.facilities as any)?.[1]?.facilityId
                        }
                      />
                    )}
                  />

                  {selectedFacility2 && (
                    <>
                      {/* Show facility info */}
                      <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Available Time Slots:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                          {(selectedFacility2.availableSlots || []).map((slot: string, index: number) => (
                            <Chip
                              key={index}
                              label={slot}
                              size="small"
                              variant="outlined"
                              color="secondary"
                            />
                          ))}
                        </Box>
                      </Box>

                      {/* Time Selection */}
                      <TextField
                        select
                        label="Time Slot"
                        fullWidth
                        size="small"
                        value={formik.values.facilities[1]?.time || ''}
                        onChange={(e) => {
                          const updatedFacilities = [...formik.values.facilities];
                          updatedFacilities[1] = {
                            ...updatedFacilities[1],
                            time: e.target.value,
                          };
                          formik.setFieldValue('facilities', updatedFacilities);
                        }}
                        error={
                          formik.touched.facilities?.[1]?.time &&
                          Boolean((formik.errors.facilities as any)?.[1]?.time)
                        }
                        helperText={
                          formik.touched.facilities?.[1]?.time &&
                          (formik.errors.facilities as any)?.[1]?.time
                            ? (formik.errors.facilities as any)?.[1]?.time
                            : !selectedDate
                            ? 'Please select a date first'
                            : availableTimeSlots2.length === 0
                            ? 'No available time slots for selected date'
                            : `${availableTimeSlots2.length} time slot(s) available`
                        }
                        disabled={!selectedDate || availableTimeSlots2.length === 0}
                      >
                        {availableTimeSlots2.map((slot: string) => (
                          <MenuItem key={slot} value={slot}>
                            {slot}
                          </MenuItem>
                        ))}
                      </TextField>
                    </>
                  )}
                </Stack>
              </Box>

              <Divider />

              {/* Form Actions */}
              <Box display="flex" gap={2}>
                <Button variant="contained" fullWidth  onClick={(e:any)=>formik.handleSubmit(e)}>
                  {isEditMode ? 'Update' : 'Create'}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleCloseDrawer}
                  type="button"
                >
                  Cancel
                </Button>
              </Box>
            </Stack>
          </>
          )}
        </CustomDrawer>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete booking "{selectedBooking?.bookingId}"? This
              action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Loading Backdrop */}
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    </LocalizationProvider>
  );
};
